import { Editor, Element, Node, Path, Range, Text, Transforms } from 'slate';
import {
  CustomEditor,
  CustomElement,
  EntityCategoryElement,
  EntityElement,
} from './extensions';
import { RichTextChunk } from '@texas/api/endpoints/chat/richTextTypes';
import { ReactEditor } from 'slate-react';
import { ChatMessageEntityLinkType } from '@texas/api/endpoints/chat/chatApi';
import { EntityItem } from './types';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';

export function replacePrevInlineWith(
  editor: Editor,
  newNode: Element,
  { addTrailingSpace = false }: { addTrailingSpace?: boolean } = {},
) {
  if (!editor.selection) return;

  Editor.withoutNormalizing(editor, () => {
    const prevEntry = Editor.previous(editor, {
      at: editor.selection?.focus,
      match: (n) => Element.isElement(n) && Editor.isInline(editor, n),
      voids: true,
    });
    if (!prevEntry) return;

    const [, prevPath] = prevEntry;

    const afterPrev = Editor.after(editor, prevPath);
    if (afterPrev && editor.selection) {
      const betweenRange: Range = {
        anchor: afterPrev,
        focus: editor.selection.anchor,
      };
      const betweenText = Editor.string(editor, betweenRange);
      if (/^\s*$/.test(betweenText)) {
        Transforms.delete(editor, { at: betweenRange });
      }
    }

    Transforms.removeNodes(editor, { at: prevPath });

    Transforms.insertNodes(editor, [newNode, { text: ' ' }], {
      at: prevPath,
      select: true,
    });

    if (addTrailingSpace) {
      const afterInserted = Editor.after(editor, prevPath, { voids: true });
      if (afterInserted) {
        Transforms.select(editor, afterInserted);
        Transforms.insertText(editor, ' ');
      }
    }
  });
}

export function previousNode(editor: Editor, path: Path) {
  if (!Path.hasPrevious(path)) return null;
  const prevPath = Path.previous(path);
  const prevNode = Node.get(editor, prevPath) as CustomElement & Text;
  return { node: prevNode, path: prevPath };
}

export function getFromPath(editor: Editor, path: Path) {
  return Node.get(editor, path) as CustomElement & Text;
}

export function createChunks(editor: Editor) {
  const chunks: RichTextChunk[] = [];
  let rawText = '';

  let skippedElements = 0;
  function traverse(list: RichTextChunk[], childs: CustomElement[]) {
    for (let i = 0; i < childs.length; i++) {
      const child = childs[i];

      const order = i - skippedElements;
      switch (child.type) {
        case 'entity-category':
          skippedElements++;
          break;
        case 'entity-value': {
          rawText += child.label;
          list.push({
            type: 'entity-value',
            children: [],
            order: order,
            entityId: child.entityId,
            entityType: child.entityType,
            label: child.label,
            tag: child.tag ?? null,
          });
          break;
        }
        case 'paragraph': {
          list.push({
            type: 'paragraph',
            children: [],
            order: order,
            text: '',
          });
          break;
        }
        default: {
          rawText += child.text;
          list.push({
            type: 'paragraph',
            children: [],
            order: order,
            text: child.text,
          });
          break;
        }
      }

      if (child.children && child.children.length > 0) {
        traverse(
          list[i - skippedElements].children,
          child.children as CustomElement[],
        );
      }
    }
  }

  traverse(chunks, editor.children as CustomElement[]);

  return { chunks, rawText };
}

export function resetEditorContent(editor: ReactEditor) {
  Transforms.delete(editor, {
    at: {
      anchor: Editor.start(editor, []),
      focus: Editor.end(editor, []),
    },
  });
}

export function replaceEditorContent(
  editor: ReactEditor,
  newValue: CustomElement[],
) {
  editor.children = newValue;
  editor.onChange();
}

export function appendEditorContent(
  editor: ReactEditor,
  newValue: CustomElement[],
) {
  Transforms.insertNodes(editor, newValue);
}

export function basicSlateParagraph(text: string): CustomElement[] {
  return [
    {
      type: 'paragraph',
      children: [{ text: text }],
    },
  ];
}

export function slateEntity(
  item: EntityItem,
  type: ChatMessageEntityLinkType,
): CustomElement[] {
  return [
    {
      type: 'entity-value',
      entityId: item.id,
      entityType: ensureEnumNumber(ChatMessageEntityLinkType, type),
      label: item.name,
      identifier: item.identifier,
      tag: item.tag ?? undefined,
      children: [{ text: '' }],
    } as EntityElement,
    { type: 'paragraph', children: [{ text: ' ' }] },
  ];
}

export function removeElementByEntity(editor: Editor, identifier?: string) {
  const nodes = Array.from(
    Editor.nodes(editor, {
      at: [],
      match: (n) => n.identifier === identifier,
    }),
  );

  // Remove in reverse order
  nodes.reverse().forEach(([, path]) => {
    Transforms.removeNodes(editor, { at: path });
  });
}

export function insertEntityCategory(
  editor: Editor,
  type: ChatMessageEntityLinkType,
) {
  const cat: EntityCategoryElement = {
    type: 'entity-category',
    entityType: type,
    children: [{ text: '' }],
  };

  Editor.withoutNormalizing(editor, () => {
    Transforms.insertNodes(editor, [cat, { text: '' }]);
  });
}

export const insertEntity = (
  editor: CustomEditor,
  type: ChatMessageEntityLinkType,
  id: number,
  label: string,
  identifier: string,
) => {
  const entityValue: EntityElement = {
    type: 'entity-value',
    entityId: id,
    entityType: type,
    label: label,
    identifier: identifier,
    children: [{ text: '' }],
  };

  replacePrevInlineWith(editor, entityValue, {
    addTrailingSpace: true,
  });
};
